/**
 * 知识抽查系统 - 会议详情页
 */
import React, { useState, useEffect } from 'react';
import { Card, Button, Tag, Table, message, Spin, Empty, Space, Radio, Input, Collapse, Popconfirm } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, CalendarOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined, MinusCircleOutlined, DownOutlined, DeleteOutlined } from '@ant-design/icons';
import knowledgeLotteryApi from '../services/knowledgeLotteryApi';

const { Panel } = Collapse;
const { TextArea } = Input;

const KnowledgeLotterySessionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [updatingAbsent, setUpdatingAbsent] = useState(false);
  const [answerStates, setAnswerStates] = useState({}); // 存储每个分配的答题状态
  const [expandedAnswers, setExpandedAnswers] = useState({}); // 存储展开的答案
  const [savingAll, setSavingAll] = useState(false); // 批量保存状态

  useEffect(() => {
    fetchSessionDetail();
  }, [id]);

  const fetchSessionDetail = async () => {
    setLoading(true);
    try {
      const response = await knowledgeLotteryApi.sessionApi.getSessionById(id);
      if (response.success) {
        setSession(response.data);
        if (response.data.assignments) {
          setAssignments(response.data.assignments);
          // 初始化答题状态
          const states = {};
          response.data.assignments.forEach(assignment => {
            const assignmentId = assignment.id || assignment._id;
            if (assignment.answer) {
              states[assignmentId] = {
                result: assignment.answer.result,
                notes: assignment.answer.notes || ''
              };
            } else {
              states[assignmentId] = {
                result: null,
                notes: ''
              };
            }
          });
          setAnswerStates(states);
        }
      } else {
        message.error(response.message || '获取会议详情失败');
        navigate('/knowledge-lottery');
      }
    } catch (error) {
      console.error('获取会议详情失败:', error);
      message.error(error.message || '获取会议详情失败');
      navigate('/knowledge-lottery');
    }
    setLoading(false);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}年${month}月${day}日`;
  };

  // 切换成员的休假状态
  const handleToggleAbsent = async (memberId) => {
    if (!session || updatingAbsent) return;
    
    setUpdatingAbsent(true);
    try {
      // 处理 absentMemberIds，确保格式一致
      const currentAbsentIds = (session.absentMemberIds || []).map(m => {
        // 如果是对象，取 id；如果是字符串，直接使用
        return typeof m === 'object' ? (m.id || m._id || m) : m;
      }).map(id => String(id)); // 统一转为字符串
      
      const memberIdStr = String(memberId);
      
      // 切换状态：如果在请假列表中，则移除；否则添加
      const newAbsentIds = currentAbsentIds.includes(memberIdStr)
        ? currentAbsentIds.filter(id => id !== memberIdStr)
        : [...currentAbsentIds, memberIdStr];
      
      // 更新会议，只更新 absentMemberIds
      const response = await knowledgeLotteryApi.sessionApi.updateSession(id, {
        absentMemberIds: newAbsentIds
      });
      
      if (response.success) {
        message.success('成员状态已更新');
        fetchSessionDetail(); // 重新获取详情
      } else {
        message.error(response.message || '更新失败');
      }
    } catch (error) {
      console.error('更新成员状态失败:', error);
      const errorMessage = error.response?.data?.message || error.message || '更新失败';
      message.error(errorMessage);
    } finally {
      setUpdatingAbsent(false);
    }
  };

  const handleAssignQuestions = async () => {
    try {
      const response = await knowledgeLotteryApi.sessionApi.assignQuestions(id);
      if (response.success) {
        message.success('抽签分配成功');
        fetchSessionDetail(); // 重新获取详情
      } else {
        message.error(response.message || '抽签分配失败');
      }
    } catch (error) {
      message.error(error.message || '抽签分配失败');
    }
  };

  // 删除会议
  const handleDeleteSession = async () => {
    try {
      const response = await knowledgeLotteryApi.sessionApi.deleteSession(id);
      if (response.success) {
        message.success('删除成功');
        navigate('/knowledge-lottery'); // 返回首页
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      console.error('删除会议失败:', error);
      message.error(error.message || '删除失败');
    }
  };

  // 切换答案展开/收起
  const handleToggleAnswer = (assignmentId) => {
    setExpandedAnswers(prev => ({
      ...prev,
      [assignmentId]: !prev[assignmentId]
    }));
  };

  // 更新答题状态
  const handleAnswerChange = (assignmentId, field, value) => {
    setAnswerStates(prev => ({
      ...prev,
      [assignmentId]: {
        ...prev[assignmentId],
        [field]: value
      }
    }));
  };

  // 批量保存所有答题记录
  const handleSaveAllAnswers = async () => {
    // 验证所有记录是否都已填写
    const missingResults = [];
    assignments.forEach(assignment => {
      const assignmentId = assignment.id || assignment._id;
      const state = answerStates[assignmentId];
      if (!state || !state.result) {
        const member = assignment.memberId;
        missingResults.push(member?.name || '未知成员');
      }
    });

    if (missingResults.length > 0) {
      message.warning(`请为以下成员选择答题结果：${missingResults.join('、')}`);
      return;
    }

    setSavingAll(true);
    try {
      const savePromises = assignments.map(async (assignment) => {
        const assignmentId = assignment.id || assignment._id;
        const state = answerStates[assignmentId];
        
        if (!state || !state.result) {
          return { success: true, skipped: true };
        }

        // 先尝试查找是否已有答案记录
        let existingAnswerId = null;
        if (assignment.answer) {
          existingAnswerId = assignment.answer.id || assignment.answer._id;
        } else {
          // 如果没有在分配记录中找到答案，尝试通过 assignmentId 查询
          try {
            const answersResponse = await knowledgeLotteryApi.answerApi.getAllAnswers({ assignmentId });
            if (answersResponse.success && answersResponse.data && answersResponse.data.length > 0) {
              existingAnswerId = answersResponse.data[0].id || answersResponse.data[0]._id;
            }
          } catch (e) {
            // 查询失败，继续尝试创建
          }
        }

        if (existingAnswerId) {
          // 更新答案
          try {
            const response = await knowledgeLotteryApi.answerApi.updateAnswer(existingAnswerId, {
              result: state.result,
              notes: state.notes || undefined
            });
            return response;
          } catch (error) {
            // 如果更新失败（可能是答案不存在），尝试创建
            if (error.response?.status === 404) {
              existingAnswerId = null;
            } else {
              throw error;
            }
          }
        }

        // 创建答案（如果没有找到现有答案）
        if (!existingAnswerId) {
          try {
            const response = await knowledgeLotteryApi.answerApi.createAnswer({
              assignmentId: assignmentId,
              result: state.result,
              notes: state.notes || undefined
            });
            return response;
          } catch (error) {
            // 如果创建失败是因为已存在，尝试查找并更新
            if (error.response?.data?.message?.includes('已经提交过答案') || 
                error.response?.data?.message?.includes('已提交过答案')) {
              // 查找现有答案并更新
              try {
                const answersResponse = await knowledgeLotteryApi.answerApi.getAllAnswers({ assignmentId });
                if (answersResponse.success && answersResponse.data && answersResponse.data.length > 0) {
                  const answerId = answersResponse.data[0].id || answersResponse.data[0]._id;
                  const updateResponse = await knowledgeLotteryApi.answerApi.updateAnswer(answerId, {
                    result: state.result,
                    notes: state.notes || undefined
                  });
                  return updateResponse;
                }
              } catch (e) {
                // 查找失败，返回原始错误
                return { success: false, error: error.response?.data?.message || error.message };
              }
            }
            throw error;
          }
        }
      });

      const results = await Promise.all(savePromises);
      const failed = results.filter(r => !r.success && !r.skipped);
      
      if (failed.length > 0) {
        message.error(`有 ${failed.length} 条记录保存失败`);
      } else {
        message.success('所有答题记录已保存');
        fetchSessionDetail();
      }
    } catch (error) {
      console.error('批量保存答题记录失败:', error);
      const errorMessage = error.response?.data?.message || error.message || '保存失败';
      message.error(errorMessage);
    } finally {
      setSavingAll(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!session) {
    return (
      <div style={{ padding: '48px' }}>
        <Empty description="会议不存在" />
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <Button onClick={() => navigate('/knowledge-lottery')}>
            返回首页
          </Button>
        </div>
      </div>
    );
  }


  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Header */}
      <header style={{ 
        borderBottom: '4px solid #ff4d4f', 
        background: '#fff',
        padding: '16px 24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button 
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/knowledge-lottery')}
              style={{ border: 'none' }}
            >
              返回
            </Button>
            <h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
              会议详情
            </h1>
          </div>
          <Popconfirm
            title="确定要删除这个会议吗？"
            description="删除后将无法恢复，相关的抽签分配和答题记录也会被删除。"
            onConfirm={handleDeleteSession}
            okText="确定删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button 
              danger 
              icon={<DeleteOutlined />}
              style={{ marginLeft: '16px' }}
            >
              删除会议
            </Button>
          </Popconfirm>
        </div>
      </header>

      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        {/* 知识点详细内容（支持多个知识点） */}
        {((session.knowledgeContentIds && session.knowledgeContentIds.length > 0) || 
          (session.knowledgeContentId && typeof session.knowledgeContentId === 'object')) && (
          <>
            {/* 获取所有知识点 */}
            {(() => {
              const allKnowledgeContents = [];
              // 新字段：多个知识点
              if (session.knowledgeContentIds && session.knowledgeContentIds.length > 0) {
                allKnowledgeContents.push(...session.knowledgeContentIds);
              }
              // 兼容旧字段：单个知识点
              if (session.knowledgeContentId && typeof session.knowledgeContentId === 'object') {
                const oldId = session.knowledgeContentId._id || session.knowledgeContentId.id;
                if (!allKnowledgeContents.some(kc => (kc._id || kc.id) === oldId)) {
                  allKnowledgeContents.push(session.knowledgeContentId);
                }
              }
              
              return allKnowledgeContents.map((knowledgeContent, index) => (
                <Card 
                  key={knowledgeContent._id || knowledgeContent.id || index}
                  style={{ 
                    border: '2px solid #ff4d4f',
                    borderRadius: '8px',
                    marginBottom: '24px'
                  }}
                >
                  <div style={{ marginBottom: '16px' }}>
                    <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '12px' }}>
                      {knowledgeContent.title || '知识点详情'}
                      {allKnowledgeContents.length > 1 && (
                        <Tag color="blue" style={{ marginLeft: '8px' }}>
                          知识点 {index + 1}/{allKnowledgeContents.length}
                        </Tag>
                      )}
                    </h2>
                    <div style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CalendarOutlined /> {formatDate(
                        knowledgeContent.associatedSession?.sessionDate || 
                        knowledgeContent.learnedDate || 
                        session.sessionDate || 
                        session.createdAt
                      )}
                      {knowledgeContent.isLearned && (
                        <Tag color="success" style={{ margin: 0 }}>已学习</Tag>
                      )}
                    </div>
                    {knowledgeContent.sessionYear && knowledgeContent.sessionNumber && (
                      <div style={{ marginTop: '8px' }}>
                        <Tag color="red" style={{ border: '2px solid #ff4d4f' }}>
                          {knowledgeContent.sessionYear}年第{knowledgeContent.sessionNumber}期会议
                        </Tag>
                      </div>
                    )}
                  </div>
                  
                  {/* 知识点内容 */}
                  {knowledgeContent.content && (
                    <div style={{ 
                      marginTop: '16px',
                      padding: '16px',
                      background: '#fafafa',
                      borderRadius: '4px',
                      border: '1px solid #e8e8e8',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap',
                      lineHeight: '1.8',
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      {knowledgeContent.content}
                    </div>
                  )}
                </Card>
              ));
            })()}
          </>
        )}

        {/* 会议信息与抽签 */}
        <Card 
          style={{ 
            border: '2px solid #ff4d4f',
            borderRadius: '8px',
            marginBottom: '24px'
          }}
        >
          <div style={{ marginBottom: '16px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '12px' }}>
              {session.title}
            </h2>
            <div style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace', marginBottom: '8px' }}>
              <CalendarOutlined /> {formatDate(session.sessionDate || session.createdAt)}
            </div>
            <div style={{ marginTop: '8px' }}>
              {(() => {
                // 根据会议日期自动计算状态
                let computedStatus = session.status;
                if (session.sessionDate) {
                  const sessionDate = new Date(session.sessionDate);
                  const now = new Date();
                  sessionDate.setHours(0, 0, 0, 0);
                  now.setHours(0, 0, 0, 0);
                  computedStatus = now <= sessionDate ? 'pending' : 'completed';
                }
                return (
                  <Tag color={computedStatus === 'completed' ? 'success' : 'processing'}>
                    {computedStatus === 'completed' ? '已完成' : '待进行'}
                  </Tag>
                );
              })()}
            </div>
            {session.description && (
              <div style={{ marginTop: '12px', color: '#666' }}>
                {session.description}
              </div>
            )}
          </div>

          {session.participantIds && session.participantIds.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                参与成员：
                {(() => {
                  // 根据会议日期判断是否允许编辑
                  let isPending = session.status === 'pending';
                  if (session.sessionDate) {
                    const sessionDate = new Date(session.sessionDate);
                    const now = new Date();
                    sessionDate.setHours(0, 0, 0, 0);
                    now.setHours(0, 0, 0, 0);
                    isPending = now <= sessionDate;
                  }
                  return isPending && (
                    <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>
                      （点击成员标签可标记为休假，休假的成员不参与抽查）
                    </span>
                  );
                })()}
              </div>
              <Space wrap>
                {session.participantIds.map((member, index) => {
                  // 处理 memberId，确保格式一致
                  const memberId = typeof member === 'object' ? (member.id || member._id || member) : member;
                  const memberIdStr = String(memberId);
                  
                  // 处理 absentMemberIds，确保格式一致
                  const absentMemberIds = (session.absentMemberIds || []).map(m => {
                    return typeof m === 'object' ? String(m.id || m._id || m) : String(m);
                  });
                  
                  const isAbsent = absentMemberIds.includes(memberIdStr);
                  
                  return (
                    <Tag
                      key={memberIdStr || index}
                      icon={<UserOutlined />}
                      onClick={(() => {
                        // 根据会议日期判断是否允许编辑
                        let isPending = session.status === 'pending';
                        if (session.sessionDate) {
                          const sessionDate = new Date(session.sessionDate);
                          const now = new Date();
                          sessionDate.setHours(0, 0, 0, 0);
                          now.setHours(0, 0, 0, 0);
                          isPending = now <= sessionDate;
                        }
                        return isPending && !updatingAbsent ? () => handleToggleAbsent(memberIdStr) : undefined;
                      })()}
                      style={{
                        cursor: (() => {
                          let isPending = session.status === 'pending';
                          if (session.sessionDate) {
                            const sessionDate = new Date(session.sessionDate);
                            const now = new Date();
                            sessionDate.setHours(0, 0, 0, 0);
                            now.setHours(0, 0, 0, 0);
                            isPending = now <= sessionDate;
                          }
                          return isPending && !updatingAbsent ? 'pointer' : 'default';
                        })(),
                        padding: '4px 12px',
                        fontSize: '14px',
                        opacity: updatingAbsent ? 0.6 : 1,
                        userSelect: 'none'
                      }}
                      color={isAbsent ? 'red' : 'blue'}
                    >
                      {typeof member === 'object' ? (member.name || member) : member}
                      {isAbsent && ' [休假]'}
                    </Tag>
                  );
                })}
              </Space>
              {session.absentMemberIds && session.absentMemberIds.length > 0 && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                  休假成员：{session.absentMemberIds.length} 人
                </div>
              )}
            </div>
          )}

          {/* 抽签环节 */}
          {(() => {
            // 根据会议日期判断是否允许抽签
            let isPending = session.status === 'pending';
            if (session.sessionDate) {
              const sessionDate = new Date(session.sessionDate);
              const now = new Date();
              sessionDate.setHours(0, 0, 0, 0);
              now.setHours(0, 0, 0, 0);
              isPending = now <= sessionDate;
            }
            return isPending && assignments.length === 0;
          })() && (
            <div style={{ 
              marginTop: '24px', 
              padding: '24px',
              background: '#fff7e6',
              borderRadius: '8px',
              border: '2px dashed #ffd591'
            }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', color: '#d46b08' }}>
                安排抽签
              </div>
              <div style={{ color: '#666', marginBottom: '16px' }}>
                确认参与成员后，点击下方按钮进行抽签分配。系统将随机为每个参与成员分配一道题目。
              </div>
              <Button 
                type="primary"
                size="large"
                onClick={handleAssignQuestions}
                style={{ 
                  background: '#ff4d4f', 
                  borderColor: '#ff4d4f',
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                执行抽签分配
              </Button>
            </div>
          )}
          
          {/* 抽签完成提示 */}
          {assignments.length > 0 && (() => {
            // 根据会议日期判断是否显示提示
            let isPending = session.status === 'pending';
            if (session.sessionDate) {
              const sessionDate = new Date(session.sessionDate);
              const now = new Date();
              sessionDate.setHours(0, 0, 0, 0);
              now.setHours(0, 0, 0, 0);
              isPending = now <= sessionDate;
            }
            return isPending;
          })() && (
            <div style={{ 
              marginTop: '24px', 
              padding: '16px',
              background: '#f6ffed',
              borderRadius: '8px',
              border: '1px solid #b7eb8f'
            }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px', color: '#52c41a' }}>
                ✓ 抽签已完成
              </div>
              <div style={{ color: '#666' }}>
                已为 {assignments.length} 位成员分配题目，请在下方的"抽签结果与答题记录"区域进行答题记录。
              </div>
            </div>
          )}
        </Card>

        {/* 抽签结果与答题记录 */}
        {assignments.length > 0 && (
          <Card 
            title="抽签结果与答题记录"
            style={{ 
              border: '2px solid #ff4d4f',
              borderRadius: '8px'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {assignments.map((assignment) => {
                const assignmentId = assignment.id || assignment._id;
                const member = assignment.memberId;
                const question = assignment.quizQuestionId;
                const answer = assignment.answer;
                const state = answerStates[assignmentId] || { result: null, notes: '' };
                const isAnswerExpanded = expandedAnswers[assignmentId];
                const weekNumber = question?.weekNumber;
                
                return (
                  <Card
                    key={assignmentId}
                    style={{
                      border: '1px solid #e8e8e8',
                      borderRadius: '8px'
                    }}
                  >
                    {/* 成员姓名 */}
                    <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
                      {member?.name || '未知成员'}
                    </div>
                    
                    {/* 问题区域 */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                        <Tag color="red" style={{ border: '2px solid #ff4d4f' }}>问题</Tag>
                        {weekNumber && (
                          <Tag color="blue">第{weekNumber}周</Tag>
                        )}
                      </div>
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: '500',
                        marginBottom: '8px',
                        padding: '12px',
                        background: '#fafafa',
                        borderRadius: '4px'
                      }}>
                        {question?.question || '-'}
                      </div>
                      <Button
                        type="link"
                        icon={<DownOutlined style={{ 
                          transform: isAnswerExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s'
                        }} />}
                        onClick={() => handleToggleAnswer(assignmentId)}
                        style={{ padding: 0 }}
                      >
                        {isAnswerExpanded ? '收起答案' : '展开答案'}
                      </Button>
                      {isAnswerExpanded && question?.answer && (
                        <div style={{ 
                          marginTop: '8px',
                          padding: '12px',
                          background: '#f0f0f0',
                          borderRadius: '4px',
                          fontSize: '14px',
                          color: '#666',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {question.answer}
                        </div>
                      )}
                    </div>
                    
                    {/* 分隔线 */}
                    <div style={{ 
                      height: '1px', 
                      background: '#ff4d4f', 
                      margin: '16px 0' 
                    }} />
                    
                    {/* 记录答题情况 */}
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '12px' }}>
                        记录答题情况
                      </div>
                      <Radio.Group
                        value={state.result}
                        onChange={(e) => handleAnswerChange(assignmentId, 'result', e.target.value)}
                        style={{ marginBottom: '16px' }}
                      >
                        <Radio value="correct">
                          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '4px' }} />
                          正确
                        </Radio>
                        <Radio value="partial">
                          <MinusCircleOutlined style={{ color: '#faad14', marginRight: '4px' }} />
                          部分正确
                        </Radio>
                        <Radio value="incorrect">
                          <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: '4px' }} />
                          错误
                        </Radio>
                      </Radio.Group>
                      
                      {/* 备注 */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                          备注 (可选)
                        </div>
                        <TextArea
                          rows={3}
                          placeholder="添加备注信息..."
                          value={state.notes}
                          onChange={(e) => handleAnswerChange(assignmentId, 'notes', e.target.value)}
                        />
                      </div>
                    </div>
                  </Card>
                );
              })}
              
              {/* 统一保存按钮 */}
              <div style={{ marginTop: '24px', textAlign: 'center' }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleSaveAllAnswers}
                  loading={savingAll}
                  style={{ 
                    background: '#ff4d4f', 
                    borderColor: '#ff4d4f',
                    padding: '8px 48px',
                    fontSize: '16px',
                    height: 'auto'
                  }}
                >
                  {savingAll ? '保存中...' : '保存所有记录'}
                </Button>
              </div>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
};

export default KnowledgeLotterySessionDetail;
